/**
 * AWS Amplify v6 configuration module.
 *
 * Calls Amplify.configure() exactly once, sourcing all values from
 * import.meta.env.VITE_COGNITO_* environment variables.
 *
 * ⚠️ ARCHITECTURE NOTE: This module must only be imported from src/main.tsx.
 * Calling Amplify.configure() more than once will overwrite the first
 * configuration. All other modules should import Auth methods directly
 * from 'aws-amplify/auth'.
 *
 * This module is side-effect free except for the configure call,
 * enabling tree-shaking in test environments that mock it entirely.
 */
import { Amplify } from 'aws-amplify';

function splitAndTrim(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function configureAmplify(): void {
  const region = import.meta.env.VITE_COGNITO_REGION;
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const userPoolClientId = import.meta.env.VITE_COGNITO_USER_POOL_WEB_CLIENT_ID;
  const oauthDomain = import.meta.env.VITE_COGNITO_OAUTH_DOMAIN;
  const redirectSignIn = import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN;
  const redirectSignOut = import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT;

  const emptyVars: string[] = [];
  // VITE_COGNITO_REGION is intentionally still validated as required
  // configuration (it is documented in .env.example and used elsewhere,
  // e.g. potentially by other AWS SDK calls this app may add later) even
  // though Amplify's own CognitoUserPoolConfig type has no `region` field
  // to pass it to — Amplify v6 derives the region from the User Pool ID
  // string itself (its prefix, e.g. "us-east-1_XXXXX" IS the region).
  // Confirmed directly against @aws-amplify/core's real type definitions:
  // CognitoUserPoolConfig has no `region` property at all.
  if (!region) emptyVars.push('VITE_COGNITO_REGION');
  if (!userPoolId) emptyVars.push('VITE_COGNITO_USER_POOL_ID');
  if (!userPoolClientId) emptyVars.push('VITE_COGNITO_USER_POOL_WEB_CLIENT_ID');
  if (!oauthDomain) emptyVars.push('VITE_COGNITO_OAUTH_DOMAIN');
  if (!redirectSignIn) emptyVars.push('VITE_COGNITO_REDIRECT_SIGN_IN');
  if (!redirectSignOut) emptyVars.push('VITE_COGNITO_REDIRECT_SIGN_OUT');

  if (emptyVars.length > 0) {
    console.warn(
      `[Amplify] The following VITE_COGNITO_* environment variables are empty: ${emptyVars.join(', ')}. ` +
        'Amplify Auth will be misconfigured. Copy .env.example to .env.local and fill in the values.',
    );
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: oauthDomain,
            redirectSignIn: splitAndTrim(redirectSignIn),
            redirectSignOut: splitAndTrim(redirectSignOut),
            responseType: 'code',
            scopes: ['email', 'openid', 'profile'],
          },
        },
      },
    },
  });
}
